import { useState, useEffect } from "react";
import { useAuth } from "src/context";
import { useDealer } from "src/hooks/useDealer";
import { useSafeApiCall, useApiClient } from "src/hooks/api";
import {
  Container,
  Header,
  Title,
  SubTitle,
} from "./ApplicationProgress.styles";
import Section from "./Section";
import type { Task } from "./Section/SectionContent";
import { API_PREFIX_PATH } from "src/constants";

interface TaskType {
  id: string;
  name: string;
  los_id: string;
  status: string;
  team_members: { first_name: string; last_name: string }[];
  updated_at: string;
}
interface MilestoneType {
  name: string;
  status: string;
  tasks: TaskType[];
}

const mapSectionTitle = (name: string): string => {
  if (name === "Funding Conditions") return "PorchPass Conditions";
  return name;
};

export default ({
  applicationId,
  applicationStatus,
}: {
  applicationId: string;
  applicationStatus: string;
}) => {
  const { user: _user } = useAuth();
  const { dealer_id } = useDealer();
  const [progressData, setProgressData] = useState<MilestoneType[]>([]);
  const safeApiCall = useSafeApiCall();
  const { fetchFromApi } = useApiClient();

  useEffect(() => {
    if (!applicationId || !dealer_id) {
      return;
    }

    const path = `${API_PREFIX_PATH}/${dealer_id}/applications/${applicationId}/tasks`;
    void safeApiCall(() => fetchFromApi(path)).then((data) => {
      const response = data as { application_progress: MilestoneType[] };
      setProgressData(response.application_progress);
    });
  }, [applicationId, dealer_id, applicationStatus]);

  const sortedProgressData = progressData.map((milestone: MilestoneType) => ({
    ...milestone,
    tasks: [...milestone.tasks].sort(
      (a, b) =>
        new Date(a.updated_at).getTime() - new Date(b.updated_at).getTime(),
    ),
  }));

  return (
    <Container>
      <Header>
        <Title>Application Progress</Title>
        <SubTitle>Manage and view outstanding tasks</SubTitle>
      </Header>
      {sortedProgressData &&
        sortedProgressData.map(({ name, status, tasks }, i) => (
          <Section
            key={name}
            index={i + 1}
            title={
              mapSectionTitle(name) as
                | "Invited"
                | "Submitted"
                | "Funded"
                | "Buyer Conditions"
                | "Seller Conditions"
                | "PorchPass Conditions"
                | "Declined"
            }
            status={status as "In-Progress" | "Completed" | "To-Do"}
            tasks={tasks as Task[]}
            isNextSection={
              status === "In-Progress" &&
              (i === 0 || sortedProgressData[i - 1].status !== "In-Progress")
            }
          />
        ))}
    </Container>
  );
};
